// Human-readable labels + dropdown options for the contract-signing enums
// (CWA-78). Raw snake_case identifiers must never reach the screen
// (feedback: no technical jargon in UX). Unknown/legacy values fall back to a
// humanized form rather than crashing.

import {
  DelayReasonCode,
  LeadSource,
  Salutation,
  SignMethod,
} from "../types/onboarding";

export interface Option<T extends string> {
  value: T;
  label: string;
}

// Turn an unexpected enum token (e.g. "COLD_CALL") into "Cold call".
export const humanizeEnum = (value: string): string =>
  value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

// Generic label lookup with a humanized fallback for values not in the map.
const labelFor = <T extends string>(
  map: Record<T, string>,
  value: T | null | undefined
): string => {
  if (!value) return "—";
  return map[value] ?? humanizeEnum(value);
};

// ---- Salutation ----
export const SALUTATION_LABELS: Record<Salutation, string> = {
  HERR: "Herr",
  FRAU: "Frau",
};
export const SALUTATION_OPTIONS: Option<Salutation>[] = [
  { value: "HERR", label: SALUTATION_LABELS.HERR },
  { value: "FRAU", label: SALUTATION_LABELS.FRAU },
];
export const salutationLabel = (v: Salutation | null | undefined): string =>
  labelFor(SALUTATION_LABELS, v);

// ---- Signature method ----
export const SIGN_METHOD_LABELS: Record<SignMethod, string> = {
  MANUAL: "Manual (hand-signed)",
  DIGITAL: "Digital e-signature",
  CLICK_TO_SIGN: "Sign via Hub link",
};
export const SIGN_METHOD_OPTIONS: Option<SignMethod>[] = [
  { value: "MANUAL", label: SIGN_METHOD_LABELS.MANUAL },
  { value: "DIGITAL", label: SIGN_METHOD_LABELS.DIGITAL },
  { value: "CLICK_TO_SIGN", label: SIGN_METHOD_LABELS.CLICK_TO_SIGN },
];
export const signMethodLabel = (v: SignMethod | null | undefined): string =>
  labelFor(SIGN_METHOD_LABELS, v);

// ---- Lead source ----
// Every value is labelled, including the three retired ones: prospects captured
// under the original option set still hold them, and a raw identifier must never
// reach the screen (CWA-154 finding 2).
export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  GOOGLE_SEARCH: "Google search",
  RECOMMENDATION: "Recommendation",
  ADVERTISING: "Advertising",
  CHATGPT: "ChatGPT",
  GEMINI: "Gemini",
  CLAUDE: "Claude",
  OTHER: "Other",
  // Retired — readable, never offered.
  ONLINE_SEARCH: "Online search",
  REFERRAL: "Referral",
  TRADE_SHOW: "Trade show",
};

// Only what the form offers, in its display order. A prospect holding a retired
// value keeps it until an agent deliberately picks a current one.
export const LEAD_SOURCE_OPTIONS: Option<LeadSource>[] = [
  { value: "GOOGLE_SEARCH", label: LEAD_SOURCE_LABELS.GOOGLE_SEARCH },
  { value: "RECOMMENDATION", label: LEAD_SOURCE_LABELS.RECOMMENDATION },
  { value: "ADVERTISING", label: LEAD_SOURCE_LABELS.ADVERTISING },
  { value: "CHATGPT", label: LEAD_SOURCE_LABELS.CHATGPT },
  { value: "GEMINI", label: LEAD_SOURCE_LABELS.GEMINI },
  { value: "CLAUDE", label: LEAD_SOURCE_LABELS.CLAUDE },
  { value: "OTHER", label: LEAD_SOURCE_LABELS.OTHER },
];

// The answer whose free-text follow-up ("please specify") is required. It used to
// be REFERRAL; the form moved it to OTHER.
export const LEAD_SOURCE_NEEDING_DETAIL: LeadSource = "OTHER";
export const leadSourceLabel = (v: LeadSource | null | undefined): string =>
  labelFor(LEAD_SOURCE_LABELS, v);

// ---- Delay reason ----
export const DELAY_REASON_LABELS: Record<DelayReasonCode, string> = {
  PROSPECT_UNAVAILABLE: "Prospect unavailable",
  NEEDS_CLARIFICATION: "Needs clarification",
  NO_RESPONSE: "No response",
  PRICE_CONCERN: "Price concern",
  TECHNICAL_ISSUE: "Technical issue",
};
export const DELAY_REASON_OPTIONS: Option<DelayReasonCode>[] = [
  { value: "PROSPECT_UNAVAILABLE", label: DELAY_REASON_LABELS.PROSPECT_UNAVAILABLE },
  { value: "NEEDS_CLARIFICATION", label: DELAY_REASON_LABELS.NEEDS_CLARIFICATION },
  { value: "NO_RESPONSE", label: DELAY_REASON_LABELS.NO_RESPONSE },
  { value: "PRICE_CONCERN", label: DELAY_REASON_LABELS.PRICE_CONCERN },
  { value: "TECHNICAL_ISSUE", label: DELAY_REASON_LABELS.TECHNICAL_ISSUE },
];
export const delayReasonLabel = (
  v: DelayReasonCode | null | undefined
): string => labelFor(DELAY_REASON_LABELS, v);
